/**
 * Delivery health of a member's email address.
 *
 * SES tells us when a message hard-bounces or gets marked as spam
 * (server/api/webhooks/ses.post.ts records it). Two things use that:
 *
 * 1. Sends skip addresses that are known dead. Repeatedly mailing them is what
 *    degrades a sending reputation, and that reputation is shared with dues
 *    receipts and password resets.
 * 2. Captains see which of their members are unreachable, so they can chase it
 *    another way instead of assuming the message landed.
 */

/** Bounce classifications SES reports, plus complaints. */
export type EmailBounceType = 'Permanent' | 'Transient' | 'Complaint'

export interface EmailHealthLike {
  email_bounce_type?: EmailBounceType
  email_bounce_reason?: string
}

/**
 * True when we should stop sending to this address.
 *
 * `Transient` is excluded on purpose: a full mailbox or a server that was down
 * is not a dead address, and dropping those members would quietly cut them out
 * of krewe communication over a temporary blip.
 */
export function isEmailUndeliverable(member: EmailHealthLike | null | undefined): boolean {
  const type = member?.email_bounce_type
  return type === 'Permanent' || type === 'Complaint'
}

/** Short label for the members table and the composer's skip notice. */
export function emailHealthLabel(member: EmailHealthLike | null | undefined): string | null {
  switch (member?.email_bounce_type) {
    case 'Permanent':
      return 'Address rejected'
    case 'Complaint':
      return 'Marked as spam'
    case 'Transient':
      return 'Delivery delayed'
    default:
      return null
  }
}

/** Longer explanation, used in tooltips. */
export function emailHealthDetail(member: EmailHealthLike | null | undefined): string | null {
  const reason = member?.email_bounce_reason?.trim()
  switch (member?.email_bounce_type) {
    case 'Permanent':
      return `This address is rejecting mail, so we no longer send to it.${reason ? ` Reason: ${reason}` : ''}`
    case 'Complaint':
      return 'This member marked krewe email as spam, so we no longer send to them.'
    case 'Transient':
      return `Delivery failed temporarily; we still send to this address.${reason ? ` Reason: ${reason}` : ''}`
    default:
      return null
  }
}

/**
 * Maps an SES event to what we store. SES sends `bounceType` on bounces and
 * nothing comparable on complaints, so complaints become their own type.
 */
export function classifySesEvent(
  eventType: string,
  bounceType?: string,
): EmailBounceType | null {
  if (eventType === 'Complaint') return 'Complaint'
  if (eventType !== 'Bounce') return null
  return bounceType === 'Permanent' ? 'Permanent' : 'Transient'
}
